import { createFetchOptions } from "./CacheConfig";
import {
  getWPHeaders,

} from "./ApiConfig";
import {
  FormDataState,
  whitePaperAndPortfolioInterface,
  ContactFormPayload,
  WPPost,
} from "@/lib/Types";
// lib/apiClient.ts

// 1. Define the API response shape
export interface TestimonialApiResponse {
  items: Array<{
    companyName: string;
    tagline: string;
    companyLogoSrc: string;
    ratingBadgeSrc: string;
    quote: string;
    authorName: string;
    authorTitle: string;
    authorAvatarSrc: string;
    variant: "white" | "gray";
    // add any other fields your backend returns
  }>;
  total?: number;
  page?: number;
  limit?: number;
}

export type ApiError = {
  message?: string;
  status?: number;
  code?: string;
};

export const WP_BASE_URL = process.env.NEXT_PUBLIC_WP_BASE_URL
export const BASE_URL = process.env.NEXT_PUBLIC_PROXY_BASE_URL
export const WP_CUSTOM_BASE_URL = process.env.NEXT_PUBLIC_WP_CUSTOM_BASE_URL

if (!WP_BASE_URL) {
  console.error("NEXT_PUBLIC_WP_BASE_URL env is Required");
}

if (!BASE_URL) {
  console.error("NEXT_PUBLIC_PROXY_BASE_URL env is Required");
}

if (!WP_CUSTOM_BASE_URL) {
  console.error("NEXT_PUBLIC_WP_CUSTOM_BASE_URL env is Required");
}

// function getEncryptedKey(): string {
//   const encryptionIV = "a6dFFTGejixv3byS5tasyA==";
//   const secret = "4fH7M9rTxhN2D0qGzLve6X2cJW9A3v5VlRb8nY+0WmI=";
//   if (!secret) {
//     throw new Error("SECRET_KEY is missing in environment variables");
//   }
//   if (!encryptionIV) {
//     throw new Error(
//       "CUSTOM_API_ENCRYPTION_IV is missing in environment variables"
//     );
//   }

//   const key = CryptoJS.enc.Base64.parse(secret);
//   const encrypted = CryptoJS.AES.encrypt(
//     CryptoJS.enc.Utf8.parse(secret),
//     key,
//     {
//       iv: CryptoJS.enc.Base64.parse(encryptionIV),
//       mode: CryptoJS.mode.CBC,
//       padding: CryptoJS.pad.Pkcs7,
//     }
//   ).toString();
//   return encrypted;
// }

// Fetch blog post by slug
export async function getBlogPostWithSlug(
  slug: string,
  timeoutMs = 10000
): Promise<WPPost[] | null> {
  const url = `${WP_BASE_URL}/posts?slug=${encodeURIComponent(slug)}&_embed`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...createFetchOptions("BLOG"),
      method: "GET",
      headers: getWPHeaders(),
      signal: controller.signal, // ✅ enable timeout abort
    });

    if (!response.ok) {
      throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
    }
    const data: WPPost[] = await response.json();
    return data.length ? data : null;

  } catch (error: unknown) {
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        console.error("getBlogPostWithSlug ==> Request timed out");
      } else {
        console.error(`getBlogPostWithSlug ==> ${error.message}`);
      }
    } else {
      console.error("getBlogPostWithSlug ==> Unknown error");
    }

    return null; // ✅ consistent with return type
  } finally {
    clearTimeout(timeoutId);
  }
}

// Fetch all blogs with optional category filter and pagination
export async function getAllBlogsWithCategories(
  categoryParam: string,
  pageNum: number,
  perPage: number,
  timeoutMs = 5000
) {
  const url = `${WP_BASE_URL}/posts?per_page=${perPage}&page=${pageNum}${categoryParam}&orderby=modified&order=desc&_embed`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      ...createFetchOptions("BLOG"),
      method: "GET",
      headers: getWPHeaders(),
      signal: controller.signal, // attach abort signal
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      console.error(`getAllBlogsWithCategories ==> HTTP ${res.status}`);
      return [];
    }

    const data = await res.json();
    return data;
  } catch (error: unknown) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === "AbortError") {
        console.error("getAllBlogsWithCategories ==> Request timed out");
      } else {
        console.error(`getAllBlogsWithCategories ==> ${error.message}`);
      }
    }

    return [];
  }
}

// Fetch all categories
export async function getAllCategoriesList(timeoutMs = 5000) {
  const url = `${WP_BASE_URL}/categories?page=1&per_page=15`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      ...createFetchOptions("STATIC"),
      method: "GET",
      headers: getWPHeaders(),
      signal: controller.signal, // timeout control
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      console.error(`getAllCategoriesList ==> HTTP ${res.status}`);
      return [];
    }

    const data = await res.json();
    return data;
  } catch (error: unknown) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        console.error("getAllCategoriesList ==> Request timed out");
      } else {
        console.error(`getAllCategoriesList ==> ${error.message}`);
      }
    }
    return [];
  }
}

// Fetch latest blogs
export async function getLatestBlogsList(timeoutMs = 5000) {
  const url = `${WP_CUSTOM_BASE_URL}/latest-posts`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const data = await fetch(url, {
      ...createFetchOptions("BLOG"),
      method: "GET",
      headers: getWPHeaders(),
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    return data;
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getLatestBlogsList ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId); // ✅ safe cleanup
  }
}

// Fetch latest blogs
export async function getLatestBlogs(timeoutMs = 8000) {
  const url = `${WP_BASE_URL}/posts`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      ...createFetchOptions("BLOG"),
      method: "GET",
      headers: getWPHeaders(),
      signal: controller.signal, // ✅ attach abort signal
    });

    if (!response.ok) {
      throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        console.error("getLatestBlogs ==> Request timed out");
      } else {
        console.error(`getLatestBlogs ==> ${error.message}`);
      }
    } else {
      console.error("getLatestBlogs ==> Unknown error");
    }

    return [];
  } finally {
    clearTimeout(timeoutId); // ✅ safe cleanup
  }
}

// Fetch related blog posts by tag or category ID
export async function gethRelatedBlogPost(
  ID: number | string,
  type: "tag" | "category",
  timeoutMs = 10000
): Promise<any[]> {
  const query = type === "tag" ? `tag_id=${ID}` : `category_id=${ID}`;
  const url = `${WP_CUSTOM_BASE_URL}/get-latest-posts?${query}&per_page=2`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;

  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const res = await fetch(url, {
      ...createFetchOptions("BLOG"),
      method: "GET",
      headers: getWPHeaders(),
      signal: controller.signal,
    });

    if (!res.ok) {
      console.error(`gethRelatedBlogPost ==> HTTP ${res.status}`);
      return [];
    }

    const data = await res.json();

    // handle API-specific edge case
    if (data?.code === "no_category") {
      return [];
    }

    return Array.isArray(data) ? data : [];
  } catch (error: unknown) {
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        console.error("gethRelatedBlogPost ==> Request timed out");
      } else {
        console.error(`gethRelatedBlogPost ==> ${error.message}`);
      }
    } else {
      console.error("gethRelatedBlogPost ==> Unknown error");
    }

    return [];
  } finally {
    clearTimeout(timeoutId!);
  }
}

export async function getSurveyDetailsByID(ID: string, type: string = "survey",timeoutMs = 5000, ) {

  const url = `${BASE_URL}/testimonial/${type == "survey" ? "survey" : "summary"}/${ID}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const data = await fetch(url, {
      ...createFetchOptions("DYNAMIC"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    return data;
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getTestimonialDetailByID ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!); // guaranteed cleanup
  }
}

// Fetch testimonial details by ID
export async function getTestimonialDetailByID(ID: number, timeoutMs = 5000) {
  const url = `${BASE_URL}/testimonial/${ID}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const data = await fetch(url, {
      ...createFetchOptions("TESTIMONIALS"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    return data;
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getTestimonialDetailByID ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!); // guaranteed cleanup
  }
}

// Fetch three latest blogs
export async function getThreeLatestBlogs(
  timeoutMs = 5000
): Promise<any[]> {
  const url = `${WP_CUSTOM_BASE_URL}/latest-three-blogs`;

  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;

  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const res = await fetch(url, {
      method: "GET",
      headers: getWPHeaders(),
      signal: controller.signal,
      cache: "no-store",
    });

    if (!res.ok) {
      console.error(`getThreeLatestBlogs ==> HTTP ${res.status}`);
      return [];
    }

    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (error: unknown) {
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        console.error("getThreeLatestBlogs ==> Request timed out");
      } else {
        console.error(`getThreeLatestBlogs ==> ${error.message}`);
      }
    } else {
      console.error("getThreeLatestBlogs ==> Unknown error");
    }

    return [];
  } finally {
    clearTimeout(timeoutId!); // guaranteed cleanup
  }
}

// Pipeline APIs //

// Fetch all portfolio list
export async function getAllPortfolioList(page: number, limit: number,
  timeoutMs = 5000
) {
  const url = `${BASE_URL}/portfolio?page=${page}&limit=${limit}`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const data = await fetch(url, {
      ...createFetchOptions("PORTFOLIO"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());

    if (data?.success) {
      return data?.data?.items;
    } else {
      return [];
    }
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getAllPortfolioList ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!); // guaranteed cleanup
  }
}

// Fetch portfolio details by ID
export async function getPortfolioDetailsByID(ID: number, timeoutMs = 5000) {
  const url = `${BASE_URL}/portfolio/${ID}`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      ...createFetchOptions("PORTFOLIO"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    if (data?.success) {
      return data?.data;
    } else {
      return [];
    }
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getPortfolioDetailsByID ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!);
  }
}

// Fetch all portfolio list
export async function getManagerList(
  pageNo: number,
  limit: number,
  type: string,
  timeoutMS = 5000
) {
  const url = `${BASE_URL}/manager?page=${pageNo}&limit=${limit}&managerType=${type}`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  timeoutId = setTimeout(() => controller.abort(), timeoutMS);
  try {
    const data = await fetch(url, {
      ...createFetchOptions("STATIC"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    if (data?.success) {
      return data?.data?.portfolios;
    } else {
      return [];
    }
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getManagerList ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

// Fetch testimonial list with pagination
export async function getTestimonialList(
  currentPages: number,
  totalLimit: number,
  timeoutMs = 5000
) {
  const url = `${BASE_URL}/testimonial?page=${currentPages}&limit=${totalLimit}`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      ...createFetchOptions("TESTIMONIALS"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    if (!data?.success) return [];
    return {
      items: data?.data?.items,
      totalPages: data?.totalPages,
      currentPage: data?.currentPage,
    };
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getTestimonialList ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

// Fetch testimonial by ID with pagination
export async function getTestimonialListByID(ID: number,
  timeoutMs = 5000
) {
  const url = `${BASE_URL}/testimonial/${ID}`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      ...createFetchOptions("TESTIMONIALS"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    if (!data?.success) return [];
    return {
      items: data?.data?.items,
      totalPages: data?.totalPages,
      currentPage: data?.currentPage,
    };
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getTestimonialListByID ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

// Fetch Media Interview list with pagination
export async function getMediaInterViewList(
  currentPages: number,
  totalLimit: number,
  type: string,
  timeoutMs = 5000
) {
  const url = `${BASE_URL}/media-interview?page=${currentPages}&limit=${totalLimit}&type=${type}`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      ...createFetchOptions("DYNAMIC"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    if (!data?.success) return [];
    return {
      items: data?.data?.items,
      totalPages: data?.data?.totalPages,
      currentPage: data?.data?.currentPage,
      totalItems: data?.data?.totalItems,
    };
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getMediaInterViewList ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

// Fetch Media Interview by ID with pagination
export async function getMediaInterViewListByID(
  ID: number,
  timeoutMs = 5000
) {
  const url = `${BASE_URL}/media-interview/${ID}`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      ...createFetchOptions("DYNAMIC"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    if (!data?.success) return [];
    return {
      items: data?.data?.items,
      totalPages: data?.totalPages,
      currentPage: data?.currentPage,
    };
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getMediaInterViewListByID ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

// Fetch Case Study list with pagination
export async function getCaseStudyList(
  currentPages: number,
  totalLimit: number,
  timeoutMs = 5000
) {
  const url = `${BASE_URL}/case-study?page=${currentPages}&limit=${totalLimit}`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      ...createFetchOptions("PORTFOLIO"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    if (!data?.success) return [];
    return {
      items: data?.data?.items,
      totalPages: data?.totalPages,
      currentPage: data?.currentPage,
    };
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getCaseStudyList ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

// Fetch testimonial by ID with pagination
export async function getCaseStudyByID(ID: number,
  timeoutMs = 5000
) {
  const url = `${BASE_URL}/case-study/${ID}`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      ...createFetchOptions("PORTFOLIO"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    if (!data?.success) return [];
    return {
      items: data?.data?.items,
      totalPages: data?.totalPages,
      currentPage: data?.currentPage,
    };
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getCaseStudyByID ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

// Fetch Media Interview by ID with pagination
export async function getCountryList(timeoutMs = 5000) {
  const url = `${BASE_URL}/util/country-list`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      ...createFetchOptions("STATIC"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    if (!data?.success) return [];
    return {
      items: data?.data?.items,
      totalPages: data?.totalPages,
      currentPage: data?.currentPage,
    };
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getCountryList ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  }
  finally {
    clearTimeout(timeoutId!)
  }
}

// Fetch Media Interview by ID with pagination
export async function getAWSsignUrl(type: string, timeoutMs = 5000) {
  const url = `${BASE_URL}/util/s3-upload-urls?location=konstant-web&type=${type}&count=1`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    if (!data?.success) return [];
    return {
      url: data?.data[0]?.url,
      preview: data?.data[0]?.preview,
      filename: data?.data[0]?.filename,
    };
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getAWSsignUrl ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

export async function uploadFileToS3(signedUrl: string, file: File, timeoutMs = 5000) {
  const buffer = await file.arrayBuffer();
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  const contentType = file?.type ?? "application/octet-stream";
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const response = await fetch(signedUrl, {
      method: "PUT",
      body: buffer,
      headers: {
        "Content-Type": contentType,
      },
    });
    return response.ok;
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `uploadFileToS3 ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

export async function getAllJobList(page: number, limit: number, timeoutMs = 5000) {
  const url = `${BASE_URL}/jobs?page=${page}&limit=${limit}`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      ...createFetchOptions("JOBS"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    if (data?.success) {
      return data?.data
    } else {
      return []
    }
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getAllJobList ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

//Fetch Job Details By ID
export async function getJobDetailsByID(ID: string, timeoutMs = 10000) {
  const url = `${BASE_URL}/jobs/fetch-details/${ID}`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      ...createFetchOptions("JOBS"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    if (data?.success) {
      return data?.data;
    } else {
      return [];
    }
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getJobDetailsByID ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

//fetch WhitePaper List

// export async function getWhiteList(page: number, limit: number) {
//   const url = `${BASE_URL}/white-paper?page=${page}&limit=${limit}`;

//   console.group("📄 getWhiteList API Call");
//   console.log("➡️ Request URL:", url);
//   console.log("➡️ Params:", { page, limit });

//   const controller = new AbortController();
//   const timeoutId = setTimeout(() => {
//     console.warn("⏱️ Request timeout triggered (8s)");
//     controller.abort();
//   }, 8000);

//   const startTime = performance.now();

//   try {
//     const response = await fetch(url, {
//       ...createFetchOptions("STATIC"),
//       method: "GET",
//       headers: {
//         "Content-Type": "application/json",
//         authorization: `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
//       },
//       mode: "cors",
//       credentials: "omit",
//       signal: controller.signal,
//     });

//     console.log("📡 Response Status:", response.status);
//     console.log("📡 Response OK:", response.ok);

//     const rawText = await response.text();
//     console.log("📦 Raw Response:", rawText);

//     let data;
//     try {
//       data = JSON.parse(rawText);
//     } catch (parseError) {
//       console.error("❌ JSON Parse Error:", parseError);
//       throw new Error("Invalid JSON response");
//     }

//     const endTime = performance.now();
//     console.log(`⏳ API Duration: ${(endTime - startTime).toFixed(2)}ms`);
//     console.log("✅ Parsed Data:", data);

//     clearTimeout(timeoutId);

//     if (data?.success) {
//       console.log("🎯 Success Flag True");
//       console.log("📊 Data Payload:", data.data);
//       console.groupEnd();
//       return data.data;
//     } else {
//       console.warn("⚠️ API returned success=false");
//       console.warn("⚠️ Full Response:", data);
//       console.groupEnd();
//       return [];
//     }

//   } catch (error: unknown) {
//     clearTimeout(timeoutId);

//     const endTime = performance.now();
//     console.error(`⏳ Failed after ${(endTime - startTime).toFixed(2)}ms`);

//     if (error instanceof DOMException && error.name === "AbortError") {
//       console.error("🚫 Request Aborted (Timeout)");
//     } else if (error instanceof Error) {
//       console.error("❌ Error Message:", error.message);
//       console.error("❌ Stack:", error.stack);
//     } else {
//       console.error("❌ Unknown Error:", error);
//     }

//     console.groupEnd();
//     return [];
//   }
// }

export async function getWhiteList(page: number, limit: number) {
  const url = `${BASE_URL}/white-paper?page=${page}&limit=${limit}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
  try {
    const data = await fetch(url, {
      ...createFetchOptions("STATIC"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
      signal: controller.signal,
    }).then((res) => res.json());

    clearTimeout(timeoutId);

    if (data?.success) {
      return data?.data;
    } else {
      return [];
    }
  } catch (error: unknown) {
    const err = error as ApiError;
    clearTimeout(timeoutId);
    console.log(error instanceof Error ? `getWhiteList ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  }
}

//Fetch White Paper Details By ID
export async function getWhitePaperDetailsByID(ID: string, timeoutMs = 5000) {
  const url = `${BASE_URL}/white-paper/${ID}`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      ...createFetchOptions("STATIC"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    return data;
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getWhitePaperDetailsByID ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

//Fetch Client List
export async function getClientList(page: number, limit: number, timeoutMs = 5000) {
  const url = `${BASE_URL}/client?page=${page}&limit=${limit}`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      ...createFetchOptions("STATIC"),
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
    }).then((res) => res.json());
    return data;
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `getClientList ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

//Save Portfolio And White Paper
export async function savePortfolioAndWhitPaper(
  payload: whitePaperAndPortfolioInterface,
  timeoutMs = 5000
) {
  const url = `${BASE_URL}/white-paper`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
      body: JSON.stringify(payload),
    }).then((res) => res.json());
    return data;
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `savePortfolioAndWhitPaper ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

//Save Portfolio/whitePaper
export async function saveNewsLetter(payload: { email: string | undefined }, timeoutMs = 5000) {
  const url = `${BASE_URL}/manager/subscription`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
      body: JSON.stringify(payload),
    }).then((res) => res.json());
    return data;
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `saveNewsLetter ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

// Save quote form data
export async function saveQuoteForm(payload: FormDataState, timeoutMs = 5000) {
  const url = `${BASE_URL}/quote/save`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
      body: JSON.stringify(payload),
    }).then((res) => res.json());

    return data;
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `saveQuoteForm ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

// Save career form data
export async function saveCareerForm(payload: ContactFormPayload, timeoutMs = 5000) {
  const url = `${BASE_URL}/career/save`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
      body: JSON.stringify(payload),
    }).then((res) => res.json());

    return data;
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `saveCareerForm ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}

// Save enquiry form data
export async function saveEnquiryForm(payload: ContactFormPayload, timeoutMs = 5000) {
  const url = `${BASE_URL}/enquiry/save`;
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const data = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "authorization": `${process.env.NEXT_PUBLIC_SECRET_KEY}`,
      },
      mode: "cors",
      credentials: "omit",
      body: JSON.stringify(payload),
    }).then((res) => res.json());

    return data;
  } catch (error: unknown) {
    const err = error as ApiError;
    console.log(error instanceof Error ? `saveEnquiryForm ==> ${error.message}` : err?.message ?? "Unknown error");
    return [];
  } finally {
    clearTimeout(timeoutId!)
  }
}